import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { bouwIndex, zoek as zoekFunctie, leesRecent, onthoudRecent } from "@/lib/menuZoek";
import { MODULE_META } from "@/components/GlobalSearch";

const inInvoer = (el) => el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

/** Globale zoekbalk (bestaande zoekfunctie: klanten, werkbonnen, facturen, klachten, projecten, medewerkers) — toets / focust. */
export function V3Zoek({ hubs }) {
  const nav = useNavigate();
  const ref = useRef(null);
  const volg = useRef(0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [sel, setSel] = useState(0);
  const index = useMemo(() => bouwIndex(hubs), [hubs]);
  const functies = useMemo(() => (q.trim().length >= 2 ? zoekFunctie(index, q, 4) : leesRecent(index).slice(0, 6)), [index, q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || inInvoer(document.activeElement)) return;
      e.preventDefault(); ref.current?.focus();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => {
    setSel(0);
    if (q.trim().length < 2) { setData([]); return undefined; }
    const mijn = ++volg.current;
    const t = setTimeout(() => api.get("/search", { params: { q: q.trim(), limit: 5 }, bptStil: true })
      .then(({ data: d }) => { if (mijn === volg.current) setData(d?.results || []); }).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [q]);

  const lijst = [...functies.map((f) => ({ soort: "functie", key: `f-${f.key}`, titel: f.label, sub: f.pad, url: f.to, icon: f.icon })),
    ...data.map((r) => ({ soort: "data", key: `d-${r.module}-${r.id}`, titel: r.titel, sub: r.subtitel, url: r.url, module: r.module }))];
  const kies = (it) => {
    if (!it) return;
    if (it.soort === "functie") onthoudRecent(it.url);
    nav(it.url); setQ(""); setOpen(false); ref.current?.blur();
  };
  const allesZoeken = () => { window.dispatchEvent(new CustomEvent("bpt:open-cmdk", { detail: { query: q } })); setQ(""); setOpen(false); };
  const toets = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, lijst.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); lijst[sel] ? kies(lijst[sel]) : q.trim() && allesZoeken(); }
    else if (e.key === "Escape") { e.preventDefault(); if (q) setQ(""); else { setOpen(false); ref.current?.blur(); } }
  };
  let laatsteKop = null;
  return (
    <div className="v3-gsearch" role="search" data-testid="v3-zoek">
      <Search />
      <input ref={ref} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={toets} placeholder="Zoek klant, werkbon, factuur, klacht, project of medewerker" aria-label="Zoek in BPTplus" autoComplete="off" spellCheck={false} data-testid="v3-zoek-veld" />
      <span className="v3-kbd v3-hide-m" aria-hidden="true">/</span>
      {open && (lijst.length > 0 || q.trim().length >= 2) && (
        <div className="v3-gres" role="listbox" data-testid="v3-zoek-resultaten">
          {lijst.map((it, i) => {
            const kop = it.soort === "functie" ? (q.trim().length >= 2 ? "Functies" : "Laatst gebruikt") : (MODULE_META[it.module]?.label || it.module);
            const toonKop = kop !== laatsteKop; laatsteKop = kop;
            const Icon = it.soort === "functie" ? (q.trim().length >= 2 ? it.icon || Search : Clock) : MODULE_META[it.module]?.icon || Search;
            return (
              <React.Fragment key={it.key}>
                {toonKop && <div className="v3-grp">{kop}</div>}
                <button type="button" role="option" aria-selected={i === sel} onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setSel(i)} onClick={() => kies(it)} data-testid={`v3-zoek-treffer-${i}`}>
                  <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--v3-ink-3)" }} />
                  <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{it.titel}</span>{it.sub && <span className="v3-sub block truncate">{it.sub}</span>}</span>
                </button>
              </React.Fragment>
            );
          })}
          {q.trim().length >= 2 && lijst.length === 0 && <div className="px-3 py-3 v3-sub" data-testid="v3-zoek-leeg">Niets gevonden voor "{q}".</div>}
          {q.trim() && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={allesZoeken} style={{ borderTop: "1px solid var(--v3-line)", color: "var(--v3-link)" }} data-testid="v3-zoek-alles">Alle resultaten voor "{q}" <span className="v3-kbd ml-auto">Ctrl K</span></button>}
        </div>
      )}
    </div>
  );
}
